import React, { useState, useEffect } from 'react';
import { Card, Btn as Button, Badge, Avatar, Pagination } from '../../components/ui';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { ConfirmModal } from '../../components/shared';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ViewVisitorServiceProviderModal from './ViewVisitorServiceProviderModal';
import AddEditVisitorServiceProviderModal from './AddEditVisitorServiceProviderModal';
import { useSettings } from '../../context/SettingsContext';
import { formatDateByPreference } from '../../utils/formatters';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

const SERVICE_TYPES = [
    'Maid', 'Cook', 'Driver', 'Tutor', 'Caretaker', 'Nurse',
    'Laundry', 'Milk Delivery', 'Newspaper Delivery', 'Electrician', 'Plumber', 'Other'
];

const AdminServiceProviders = () => {
    const { language = 'en' } = useSettings();
    const t = (key, params) => {
        let str = translations[language]?.service_providers?.[key] || translations.en?.service_providers?.[key];
        if (!str) {
            if (key.startsWith('type_')) return key.replace('type_', '').replace(/_/g, ' ');
            if (typeof params === 'string') return params;
            return key;
        }
        if (params && typeof params === 'object') { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
        return str;
    };

    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterServiceType, setFilterServiceType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterVerification, setFilterVerification] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, perPage: 15, lastPage: 1 });

    const [selectedProvider, setSelectedProvider] = useState(null);
    const [addEditModalOpen, setAddEditModalOpen] = useState(false);
    const [providerToEdit, setProviderToEdit] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, provider: null });
    const [statusConfirmModal, setStatusConfirmModal] = useState({ open: false, provider: null, action: null });

    useEffect(() => {
        fetchProviders();
    }, [searchTerm, filterServiceType, filterStatus, filterVerification, sortBy, sortOrder, page]);

    const fetchProviders = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/v1/visitor-service-providers', {
                params: {
                    search: searchTerm || undefined,
                    service_type: filterServiceType || undefined,
                    is_active: filterStatus !== '' ? filterStatus : undefined,
                    verification_status: filterVerification || undefined,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: page,
                    per_page: 15,
                    scope: 'all'
                }
            });

            if (response.data?.data) {
                setProviders(response.data.data);
                setPagination({
                    total: response.data.total || response.data.data.length,
                    perPage: response.data.per_page || 15,
                    lastPage: response.data.last_page || 1
                });
            } else if (Array.isArray(response.data)) {
                setProviders(response.data);
                setPagination({ total: response.data.length, perPage: 15, lastPage: 1 });
            }
        } catch (error) {
            console.error('Error fetching providers:', error);
            toast.error(error.response?.data?.message || 'Failed to load service providers');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.provider) return;
        try {
            await axios.delete(`/api/v1/visitor-service-providers/${deleteModal.provider.id}`);
            toast.success('Provider deleted successfully');
            setDeleteModal({ open: false, provider: null });
            fetchProviders();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete provider');
        }
    };

    const handleStatusUpdate = async () => {
        const { provider, action } = statusConfirmModal;
        if (!provider || !action) return;
        try {
            await axios.patch(`/api/v1/visitor-service-providers/${provider.id}/status`, { action });
            toast.success(`Provider ${action}d successfully`);
            setStatusConfirmModal({ open: false, provider: null, action: null });
            fetchProviders();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || `Failed to ${action} provider`;
            toast.error(msg);
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text("Service Providers Directory", 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()} | Total Records: ${providers.length}`, 14, 22);

        const tableColumn = ["ID", "Name", "Mobile", "Service Type", "Assigned Units", "Status", "Verification", "Created Date"];
        const tableRows = providers.map(p => {
            const assigned = (p.units || []).map(u => u.unit ? (u.unit.block ? `${u.unit.block}-${u.unit.unit_number}` : u.unit.unit_number) : '').filter(Boolean).join(', ');
            return [
                `#${p.id}`,
                p.name || '-',
                p.mobile_number || '-',
                p.service_type || '-',
                assigned || '—',
                p.is_active ? 'Active' : 'Inactive',
                (p.verification_status || 'pending').toUpperCase(),
                formatDateByPreference(p.created_at)
            ];
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 28,
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [79, 70, 229] },
        });

        doc.save(`Service_Providers_${new Date().toISOString().slice(0, 10)}.pdf`);
        toast.success("PDF report downloaded successfully!");
    };

    const handleExportExcel = () => {
        const headers = ["Provider ID", "Name", "Mobile", "Service Type", "Assigned Units", "Status", "Verification Status", "Created Date", "Last Updated"];
        const rows = providers.map(p => {
            const assigned = (p.units || []).map(u => u.unit ? (u.unit.block ? `${u.unit.block}-${u.unit.unit_number}` : u.unit.unit_number) : '').filter(Boolean).join(', ');
            return [
                `#${p.id}`,
                p.name || "",
                p.mobile_number || "",
                p.service_type || "",
                assigned || "None",
                p.is_active ? "Active" : "Inactive",
                (p.verification_status || "pending").toUpperCase(),
                formatDateByPreference(p.created_at),
                formatDateByPreference(p.updated_at)
            ];
        });

        const data = [headers, ...rows];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        worksheet['!cols'] = [
            { wch: 14 },
            { wch: 22 },
            { wch: 18 },
            { wch: 18 },
            { wch: 20 },
            { wch: 14 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Service Providers");
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Service_Providers_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Excel report downloaded successfully!");
    };

    const getVerificationBadge = (status) => {
        const s = (status || 'pending').toLowerCase();
        if (s === 'verified') {
            return <Badge color="green" dot sm>{t("status_verified", "Verified")}</Badge>;
        }
        if (s === 'rejected') {
            return <Badge color="red" dot sm>{t("status_rejected", "Rejected")}</Badge>;
        }
        return <Badge color="amber" dot sm>{t("status_pending", "Pending")}</Badge>;
    };

    return (
        <div className="p-3.5 sm:p-6 pb-24 lg:pb-6 text-slate-900 dark:text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-ink dark:text-slate-100">{t("title_admin_service_providers")}</h1>
                    <p className="text-xs text-muted dark:text-slate-400 mt-0.5">
                        {t("desc_service_providers", "Manage society service providers, verifications, unit assignments, and access control.")}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button sm variant="secondary" icon="download" onClick={handleExportPDF}>
                        PDF
                    </Button>
                    <Button sm variant="secondary" icon="download" onClick={handleExportExcel}>
                        Excel
                    </Button>
                    <Button sm variant="primary" icon="plus" onClick={() => { setProviderToEdit(null); setAddEditModalOpen(true); }}>
                        {t("btn_add_new", "Add Provider")}
                    </Button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-5 p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-800">
                <input
                    placeholder="Search by Name, Mobile, ID, or Unit..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary"
                />

                <select
                    value={filterServiceType}
                    onChange={(e) => { setFilterServiceType(e.target.value); setPage(1); }}
                    className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                    <option value="">{t("filter_all_types", "All Service Types")}</option>
                    {SERVICE_TYPES.map(st => (
                        <option key={st} value={st}>{t("type_" + st.toLowerCase().replace(/ /g, "_"))}</option>
                    ))}
                </select>

                <select
                    value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                    className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                    <option value="">{t("filter_all_status", "All Status")}</option>
                    <option value="true">{t("status_active", "Active")}</option>
                    <option value="false">{t("status_inactive", "Inactive")}</option>
                    <option value="blocked">Blocked Access</option>
                </select>

                <select
                    value={filterVerification}
                    onChange={(e) => { setFilterVerification(e.target.value); setPage(1); }}
                    className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-ink dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                    <option value="">{t("filter_all_verification", "All Verification")}</option>
                    <option value="verified">{t("status_verified", "Verified")}</option>
                    <option value="pending">{t("status_pending", "Pending")}</option>
                    <option value="rejected">{t("status_rejected", "Rejected")}</option>
                </select>
            </div>

            <Card className="overflow-hidden">
                {/* Desktop View: Full detailed table */}
                <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-gray-400">
                            <tr>
                                <th className="p-3.5 cursor-pointer select-none" onClick={() => handleSort('id')}>
                                    <div className="flex items-center gap-1">
                                        {t("col_provider_id", "Provider ID")}
                                        {sortBy === 'id' && (<span>{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                                    </div>
                                </th>
                                <th className="p-3.5 cursor-pointer select-none" onClick={() => handleSort('name')}>
                                    <div className="flex items-center gap-1">
                                        {t("col_provider", "Provider")}
                                        {sortBy === 'name' && (<span>{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                                    </div>
                                </th>
                                <th className="p-3.5">{t("col_assigned_unit", "Assigned Unit / Resident")}</th>
                                <th className="p-3.5 cursor-pointer select-none" onClick={() => handleSort('service_type')}>
                                    <div className="flex items-center gap-1">
                                        {t("col_service_type", "Service Type")}
                                        {sortBy === 'service_type' && (<span>{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                                    </div>
                                </th>
                                <th className="p-3.5">{t("col_mobile", "Mobile")}</th>
                                <th className="p-3.5">{t("col_verification", "Verification")}</th>
                                <th className="p-3.5">{t("col_status", "Status")}</th>
                                <th className="p-3.5 cursor-pointer select-none" onClick={() => handleSort('start_date')}>
                                    <div className="flex items-center gap-1">
                                        {t("col_dates", "Created / Start Date")}
                                        {sortBy === 'start_date' && (<span>{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                                    </div>
                                </th>
                                <th className="p-3.5 text-right">{t("col_actions", "Actions")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                            {loading ? (
                                <tr><td colSpan="9" className="p-8 text-center text-muted dark:text-slate-400">{t("text_loading", "Loading...")}</td></tr>
                            ) : providers.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="p-12 text-center text-muted dark:text-slate-400">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50 rounded-full flex items-center justify-center mb-3">
                                                <svg className="w-6 h-6 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                                </svg>
                                            </div>
                                            <div className="text-sm font-medium">{t("text_no_records_found", "No records found matching filters.")}</div>
                                        </div>
                                    </td>
                                </tr>
                            ) : providers.map(provider => {
                                const assignedUnits = (provider.units || []).map(u => {
                                    if (u.unit?.unit_number) {
                                        return u.unit.block ? `${u.unit.block}-${u.unit.unit_number}` : u.unit.unit_number;
                                    }
                                    return null;
                                }).filter(Boolean);

                                const firstResident = provider.units?.[0]?.member?.name;
                                const isBlocked = !provider.is_active && (provider.notes || '').includes('[BLOCKED]');

                                return (
                                    <tr key={provider.id} className="hover:bg-gray-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                        <td className="p-3.5 font-mono font-semibold text-primary dark:text-indigo-400">
                                            #{provider.id}
                                        </td>
                                        <td className="p-3.5">
                                            <div className="flex items-center gap-2.5">
                                                <Avatar
                                                    src={provider.photo_url}
                                                    name={provider.name}
                                                    size={32}
                                                    className="rounded-full shadow-sm flex-shrink-0"
                                                />
                                                <div>
                                                    <div className="font-semibold text-ink dark:text-slate-100">{provider.name}</div>
                                                    {provider.identity_type && (
                                                        <div className="text-[10px] text-muted dark:text-slate-400">
                                                            {provider.identity_type}: {provider.identity_number || '-'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3.5">
                                            {assignedUnits.length > 0 ? (
                                                <div>
                                                    <div className="flex flex-wrap gap-1 mb-0.5">
                                                        {assignedUnits.map((unitStr, idx) => (
                                                            <span key={idx} className="inline-block px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-semibold rounded text-[11px] border border-indigo-200/60 dark:border-indigo-800/50">
                                                                {unitStr}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    {firstResident && (
                                                        <div className="text-[10px] text-muted dark:text-slate-400">
                                                            Resident: {firstResident}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted dark:text-slate-500 italic text-[11px]">Unassigned</span>
                                            )}
                                        </td>
                                        <td className="p-3.5 text-slate-700 dark:text-slate-300 font-medium capitalize">
                                            {t("type_" + (provider.service_type || '').toLowerCase().replace(/ /g, "_"))}
                                        </td>
                                        <td className="p-3.5 font-mono text-slate-600 dark:text-slate-300">
                                            {provider.mobile_number || '—'}
                                        </td>
                                        <td className="p-3.5">
                                            {getVerificationBadge(provider.verification_status)}
                                        </td>
                                        <td className="p-3.5">
                                            {isBlocked ? (
                                                <Badge color="red" dot sm>Blocked</Badge>
                                            ) : (
                                                <Badge color={provider.is_active ? 'green' : 'gray'} dot sm>
                                                    {provider.is_active ? t("status_active", "Active") : t("status_inactive", "Inactive")}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="p-3.5 text-[11px] text-muted dark:text-slate-400">
                                            <div><span className="font-medium">Created:</span> {formatDateByPreference(provider.created_at)}</div>
                                            {provider.units?.[0]?.start_date && (
                                                <div><span className="font-medium">Start:</span> {formatDateByPreference(provider.units[0].start_date)}</div>
                                            )}
                                        </td>
                                        <td className="p-3.5 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <Button size="sm" variant="secondary" onClick={() => setSelectedProvider(provider)}>
                                                    {t("btn_view", "View")}
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => { setProviderToEdit(provider); setAddEditModalOpen(true); }}>
                                                    <Icon name="edit" size={13} />
                                                </Button>
                                                {provider.is_active ? (
                                                    <Button size="sm" variant="outline" title="Suspend Access" className="!text-amber-600 !border-amber-200 dark:!border-amber-800/60 hover:!bg-amber-50 dark:hover:!bg-amber-950/30" onClick={() => setStatusConfirmModal({ open: true, provider, action: 'deactivate' })}>
                                                        <Icon name="ban" size={13} />
                                                    </Button>
                                                ) : (
                                                    <Button size="sm" variant="outline" title="Activate Access" className="!text-emerald-600 !border-emerald-200 dark:!border-emerald-800/60 hover:!bg-emerald-50 dark:hover:!bg-emerald-950/30" onClick={() => setStatusConfirmModal({ open: true, provider, action: 'activate' })}>
                                                        <Icon name="check" size={13} />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline" title="Delete Provider" className="!text-red-500 !border-red-200 dark:!border-red-800/60 hover:!bg-red-50 dark:hover:!bg-red-950/30" onClick={() => setDeleteModal({ open: true, provider })}>
                                                    <Icon name="trash" size={13} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View: High quality swipeable responsive cards */}
                <div className="block lg:hidden divide-y divide-slate-100 dark:divide-slate-800">
                    {loading ? (
                        <div className="p-8 text-center text-muted dark:text-slate-400 text-xs">
                            {t("text_loading", "Loading...")}
                        </div>
                    ) : providers.length === 0 ? (
                        <div className="p-8 text-center text-muted dark:text-slate-400">
                            <div className="text-xs font-medium">{t("text_no_records_found", "No records found matching filters.")}</div>
                        </div>
                    ) : (
                        providers.map((provider, i) => {
                            const assignedUnits = (provider.units || []).map(u => {
                                if (u.unit?.unit_number) {
                                    return u.unit.block ? `${u.unit.block}-${u.unit.unit_number}` : u.unit.unit_number;
                                }
                                return null;
                            }).filter(Boolean);

                            const firstResident = provider.units?.[0]?.member?.name;
                            const isBlocked = !provider.is_active && (provider.notes || '').includes('[BLOCKED]');

                            return (
                                <IonItemSliding
                                    key={provider.id}
                                    className={`overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`}
                                    style={{ '--background': 'transparent' }}
                                >
                                    <IonItem
                                        lines="none"
                                        style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}
                                    >
                                        <div
                                            onClick={() => setSelectedProvider(provider)}
                                            className="w-full p-4 bg-white dark:bg-slate-900 active:bg-slate-50 dark:active:bg-slate-800/60 transition-colors cursor-pointer"
                                        >
                                            {/* Top Row: Provider Avatar, Name, ID & Badges */}
                                            <div className="flex items-start justify-between gap-3 mb-2.5">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <Avatar
                                                        src={provider.photo_url}
                                                        name={provider.name}
                                                        size={40}
                                                        className="rounded-full shadow-xs flex-shrink-0"
                                                    />
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-sm text-ink dark:text-slate-100 truncate">
                                                                {provider.name}
                                                            </span>
                                                            <span className="font-mono text-[11px] font-semibold text-primary dark:text-indigo-400 flex-shrink-0">
                                                                #{provider.id}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-muted dark:text-slate-400 mt-0.5 capitalize">
                                                            {t("type_" + (provider.service_type || '').toLowerCase().replace(/ /g, "_"))}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    {getVerificationBadge(provider.verification_status)}
                                                    {isBlocked ? (
                                                        <Badge color="red" dot sm>Blocked</Badge>
                                                    ) : (
                                                        <Badge color={provider.is_active ? 'green' : 'gray'} dot sm>
                                                            {provider.is_active ? t("status_active", "Active") : t("status_inactive", "Inactive")}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Details Grid / Chips */}
                                            <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800/70 text-xs">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-muted dark:text-slate-400 text-[11px]">
                                                        {t("col_mobile", "Mobile")}:
                                                    </span>
                                                    <span className="font-mono text-slate-700 dark:text-slate-200 font-medium">
                                                        {provider.mobile_number || '—'}
                                                    </span>
                                                </div>

                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="text-muted dark:text-slate-400 text-[11px] shrink-0 pt-0.5">
                                                        {t("col_assigned_unit", "Assigned Unit")}:
                                                    </span>
                                                    <div className="text-right min-w-0">
                                                        {assignedUnits.length > 0 ? (
                                                            <div className="flex flex-wrap items-center justify-end gap-1">
                                                                {assignedUnits.map((unitStr, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="inline-block px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-semibold rounded text-[11px] border border-indigo-200/60 dark:border-indigo-800/50"
                                                                    >
                                                                        {unitStr}
                                                                    </span>
                                                                ))}
                                                                {firstResident && (
                                                                    <div className="w-full text-[10px] text-muted dark:text-slate-400 mt-0.5">
                                                                        Resident: {firstResident}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted dark:text-slate-500 italic text-[11px]">
                                                                Unassigned
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {provider.identity_type && (
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-muted dark:text-slate-400 text-[11px]">
                                                            {provider.identity_type}:
                                                        </span>
                                                        <span className="font-mono text-slate-700 dark:text-slate-300 text-[11px]">
                                                            {provider.identity_number || '-'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action quick buttons & swipe hint footer */}
                                            <div className="flex items-center justify-between gap-2 pt-2.5 mt-2.5 border-t border-slate-100 dark:border-slate-800/60">
                                                <span className="text-[10px] text-muted dark:text-slate-500 flex items-center gap-1">
                                                    <Icon name="chevron_left" size={12} className="text-slate-400" /> Swipe left for actions
                                                </span>
                                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                    <Button size="xs" variant="secondary" onClick={() => setSelectedProvider(provider)}>
                                                        {t("btn_view", "View")}
                                                    </Button>
                                                    <Button size="xs" variant="outline" onClick={() => { setProviderToEdit(provider); setAddEditModalOpen(true); }}>
                                                        <Icon name="edit" size={12} />
                                                    </Button>
                                                    <Button size="xs" variant="outline" title="Delete Provider" className="!text-red-500 !border-red-200 dark:!border-red-800/60 hover:!bg-red-50" onClick={() => setDeleteModal({ open: true, provider })}>
                                                        <Icon name="trash" size={12} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </IonItem>

                                    <IonItemOptions side="end">
                                        <IonItemOption color="primary" onClick={() => { setProviderToEdit(provider); setAddEditModalOpen(true); }}>
                                            <div className="flex flex-col items-center justify-center w-12 h-full">
                                                <Icon name="edit" size={18} color="white" />
                                            </div>
                                        </IonItemOption>
                                        {provider.is_active ? (
                                            <IonItemOption color="warning" onClick={() => setStatusConfirmModal({ open: true, provider, action: 'deactivate' })}>
                                                <div className="flex flex-col items-center justify-center w-12 h-full">
                                                    <Icon name="ban" size={18} color="white" />
                                                </div>
                                            </IonItemOption>
                                        ) : (
                                            <IonItemOption color="success" onClick={() => setStatusConfirmModal({ open: true, provider, action: 'activate' })}>
                                                <div className="flex flex-col items-center justify-center w-12 h-full">
                                                    <Icon name="check" size={18} color="white" />
                                                </div>
                                            </IonItemOption>
                                        )}
                                        <IonItemOption color="danger" onClick={() => setDeleteModal({ open: true, provider })}>
                                            <div className="flex flex-col items-center justify-center w-12 h-full">
                                                <Icon name="trash" size={18} color="white" />
                                            </div>
                                        </IonItemOption>
                                    </IonItemOptions>
                                </IonItemSliding>
                            );
                        })
                    )}
                </div>

                {pagination.total > pagination.perPage && (
                    <div className="p-3.5 border-t border-slate-100 dark:border-slate-800 flex justify-center">
                        <Pagination
                            page={page}
                            total={pagination.total}
                            perPage={pagination.perPage}
                            onPage={setPage}
                        />
                    </div>
                )}
            </Card>

            {/* Modals */}
            {selectedProvider && (
                <ViewVisitorServiceProviderModal
                    provider={selectedProvider}
                    onClose={() => setSelectedProvider(null)}
                    onEdit={(p) => { setProviderToEdit(p); setAddEditModalOpen(true); }}
                    onStatusChange={fetchProviders}
                />
            )}

            {addEditModalOpen && (
                <AddEditVisitorServiceProviderModal
                    providerToEdit={providerToEdit}
                    onClose={() => { setAddEditModalOpen(false); setProviderToEdit(null); }}
                    onSuccess={fetchProviders}
                />
            )}

            {deleteModal.open && (
                <ConfirmModal
                    title="Delete Service Provider"
                    message={`Are you sure you want to permanently delete ${deleteModal.provider?.name}? Existing gate entry/exit logs will be preserved.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteModal({ open: false, provider: null })}
                />
            )}

            {statusConfirmModal.open && (
                <ConfirmModal
                    title={`${statusConfirmModal.action === 'activate' ? 'Activate' : 'Deactivate'} Provider Access`}
                    message={`Are you sure you want to ${statusConfirmModal.action} access for ${statusConfirmModal.provider?.name}?`}
                    confirmLabel={statusConfirmModal.action === 'activate' ? 'Activate' : 'Deactivate'}
                    danger={statusConfirmModal.action !== 'activate'}
                    onConfirm={handleStatusUpdate}
                    onCancel={() => setStatusConfirmModal({ open: false, provider: null, action: null })}
                />
            )}
        </div>
    );
};

export default AdminServiceProviders;
